import React, { useState, useEffect } from "react";
import api from "../utils/api";
import { useTenant } from "../context/TenantContext";
import "../styles/UpdatePrompt.css";
import logger from '../utils/logger';

const UpdateResendKey = () => {
  const { tenant } = useTenant();
  const tenantId = tenant?.id || window.__TENANT_ID__ || localStorage.getItem("superadmin_tenant_id");

  const [resendKey, setResendKey] = useState("");
  const [maskedKey, setMaskedKey] = useState(null);
  const [hasKey, setHasKey] = useState(false);
  const [fromEmail, setFromEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState("");
  const [fetching, setFetching] = useState(true);
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    if (tenantId) fetchConfig();
  }, [tenantId]);

  const fetchConfig = async () => {
    try {
      const res = await api.get(`/api/tenants/${tenantId}/email-config`);
      if (res.data.success) {
        setHasKey(res.data.hasKey);
        setMaskedKey(res.data.maskedKey);
        if (res.data.fromEmail) setFromEmail(res.data.fromEmail);
      }
    } catch (error) {
      logger.error("Error fetching email config:", error);
    } finally {
      setFetching(false);
    }
  };

  const handleSave = async () => {
    if (!resendKey.trim()) { setMessage("Please enter an API key"); return; }
    if (!resendKey.trim().startsWith("re_")) { setMessage("Key must start with 're_'"); return; }

    setLoading(true);
    setMessage("");

    try {
      const res = await api.put(`/api/tenants/${tenantId}/email-config`, {
        resendApiKey: resendKey.trim(),
        resendFromEmail: fromEmail.trim() || null,
      });
      if (res.data.success) {
        setMessage("Resend API key saved successfully!");
        setMaskedKey(res.data.maskedKey);
        setHasKey(true);
        setResendKey("");
        setShowKey(false);
      }
    } catch (err) {
      setMessage(`Error: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm("Remove Resend API key? Emails will use the global key.")) return;
    setDeleting(true);
    setMessage("");

    try {
      await api.delete(`/api/tenants/${tenantId}/email-config`);
      setMessage("Key removed successfully");
      setHasKey(false);
      setMaskedKey(null);
      setResendKey("");
      setFromEmail("");
    } catch (err) {
      setMessage(`Error: ${err.response?.data?.message || err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  if (fetching) {
    return (
      <div className="update-container">
        <h2>Loading configuration...</h2>
      </div>
    );
  }

  return (
    <div className="update-container">
      <h2>Resend Email API Key</h2>

      {/* Status */}
      <div style={{
        padding: "16px 20px", borderRadius: "8px", marginBottom: "24px",
        backgroundColor: hasKey ? "#f0fdf4" : "#fef2f2",
        border: `1px solid ${hasKey ? "#86efac" : "#fca5a5"}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
          <span style={{ fontSize: "20px" }}>{hasKey ? "\u2705" : "\u274C"}</span>
          <strong style={{ color: hasKey ? "#166534" : "#991b1b" }}>
            {hasKey ? "Resend Key Configured" : "No Resend Key Configured"}
          </strong>
        </div>
        {hasKey && maskedKey && (
          <div style={{ fontSize: "14px", color: "#374151" }}>
            Current Key: <code style={{ backgroundColor: "#f3f4f6", padding: "2px 8px", borderRadius: "4px" }}>{maskedKey}</code>
          </div>
        )}
        {!hasKey && (
          <p style={{ fontSize: "13px", color: "#991b1b", margin: 0 }}>
            Email sending (OTP, notifications) will use the global key until a tenant-specific key is configured.
          </p>
        )}
      </div>

      {/* Key Input */}
      <div className="prompt-section">
        <label>{hasKey ? "Update Resend API Key" : "Add Resend API Key"}</label>
        <div style={{ position: "relative" }}>
          <input
            type={showKey ? "text" : "password"}
            value={resendKey}
            onChange={(e) => setResendKey(e.target.value)}
            placeholder="re_..."
            style={{
              width: "100%", padding: "12px 50px 12px 14px", borderRadius: "8px",
              border: "1px solid #ccc", fontSize: "15px", fontFamily: "monospace", boxSizing: "border-box",
            }}
          />
          <button type="button" onClick={() => setShowKey(!showKey)}
            style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)",
              background: "none", border: "none", cursor: "pointer", fontSize: "14px", color: "#6b7280", padding: "4px" }}>
            {showKey ? "Hide" : "Show"}
          </button>
        </div>

        <p style={{ fontSize: "13px", color: "#6b7280", margin: "8px 0 0 0" }}>
          Get your API key from{" "}
          <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb" }}>
            resend.com/api-keys
          </a>
        </p>

        {/* From Email */}
        <label style={{ display: "block", fontWeight: "600", marginTop: "16px", marginBottom: "4px" }}>From Email (verified domain)</label>
        <input
          type="text"
          value={fromEmail}
          onChange={(e) => setFromEmail(e.target.value)}
          placeholder="Clinic Name <noreply@yourdomain.com>"
          style={{
            width: "100%", padding: "12px 14px", borderRadius: "8px",
            border: "1px solid #ccc", fontSize: "15px", boxSizing: "border-box",
          }}
        />
        <p style={{ fontSize: "13px", color: "#6b7280", margin: "6px 0 0 0" }}>
          Must use a domain verified in your Resend account. Example: <code>{tenant?.brandName || tenant?.name || 'Clinic'} &lt;noreply@{tenant?.customDomain || 'example.com'}&gt;</code>
        </p>

        <div style={{ display: "flex", gap: "12px", marginTop: "14px" }}>
          <button onClick={handleSave} disabled={loading || !resendKey.trim()} className={loading ? "loading" : ""} style={{ flex: 1 }}>
            {loading ? "Saving..." : hasKey ? "Update Key" : "Save Key"}
          </button>
          {hasKey && (
            <button onClick={handleRemove} disabled={deleting}
              style={{ padding: "10px 20px", background: deleting ? "#ccc" : "#dc2626", color: "white",
                border: "none", borderRadius: "8px", fontSize: "15px", cursor: deleting ? "not-allowed" : "pointer" }}>
              {deleting ? "Removing..." : "Remove Key"}
            </button>
          )}
        </div>

        {message && (
          <p className={`status-message ${message.includes("successfully") || message.includes("saved") ? "success" : "error"}`}
            style={{ marginTop: "14px", padding: "10px", borderRadius: "6px",
              backgroundColor: message.includes("successfully") || message.includes("saved") ? "#f0fdf4" : "#fef2f2",
              color: message.includes("successfully") || message.includes("saved") ? "#166534" : "#991b1b" }}>
            {message}
          </p>
        )}
      </div>

      {/* Info */}
      <div style={{
        padding: "16px 20px", borderRadius: "8px", marginTop: "24px",
        backgroundColor: "#eff6ff", border: "1px solid #bfdbfe",
        fontSize: "13px", color: "#1e40af", lineHeight: "1.6",
      }}>
        <strong>How it works:</strong>
        <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
          <li>Each sub-account uses its own Resend API key for email (OTP, notifications)</li>
          <li>The "From Email" must use a domain verified in YOUR Resend account</li>
          <li>Key is encrypted before storage</li>
          <li>Billing goes to your Resend account</li>
        </ul>
      </div>
    </div>
  );
};

export default UpdateResendKey;
